import { Auth } from "aws-amplify";

const API_BASE = process.env.REACT_APP_FORGE_API_BASE_URL || "/api";
const PAYWALL_API_BASE = `${process.env.REACT_APP_VIDERI_API_BASE_URL || ""}/paywall/api/v1`;

/**
 * Get a fresh Cognito idToken. Amplify refreshes it automatically via the
 * refresh token when needed; this always returns a valid, non-expired token.
 */
async function getToken() {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch wrapper. Adds Authorization header with the Cognito
 * idToken and handles 401 by triggering sign-out and page reload.
 */
export async function authFetch(url, options = {}) {
  const token = await getToken();
  const headers = { ...options.headers };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  try {
    const cms = localStorage.getItem("videri-forge-cms");
    if (cms) {
      const { tenant } = JSON.parse(cms);
      if (tenant) headers["x-tenant"] = tenant;
    }
  } catch { /* ignore */ }

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    try { await Auth.signOut(); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("forge:session-expired"));
    throw new Error("Session expired. Please sign in again.");
  }

  return res;
}

/**
 * Extract error message from API response data.
 * Handles both old format { error: "string" } and new format { error: { code, message } }.
 */
export function extractError(data, fallback = "Something went wrong") {
  if (!data) return fallback;
  const err = data.error;
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (typeof err === "object" && err.message) {
    if (Array.isArray(err.details) && err.details.length > 0) {
      const hints = err.details.slice(0, 3).map((d) =>
        d.path ? `${d.path}: ${d.message}` : d.message
      ).join("; ");
      return `${err.message} — ${hints}`;
    }
    return err.message;
  }
  return fallback;
}

/**
 * Decompose an edit request into layer-scoped passes.
 */
export async function decomposeEdit(message, integrationName, files) {
  const res = await authFetch(`${API_BASE}/smart-edit/decompose`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, integrationName, files }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Decomposition failed" }));
    throw new Error(extractError(err, "Failed to analyze edit request"));
  }
  return res.json();
}

/**
 * Infer a data contract from a running app's data endpoint.
 */
export async function inferContract(integrationName) {
  const res = await authFetch(`${API_BASE}/smart-edit/infer-contract`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ integrationName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Contract inference failed" }));
    throw new Error(extractError(err, "Failed to infer data contract"));
  }
  return res.json();
}

/**
 * List version snapshots for an app (newest first). `id` is the app's real
 * id (apps.id) — the backend's resolveAppId middleware also accepts a
 * legacy display name for any caller not yet updated.
 */
export async function listVersions(id) {
  const res = await authFetch(`${API_BASE}/integrations/${encodeURIComponent(id)}/versions`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed to load versions" }));
    throw new Error(extractError(err, "Failed to load versions"));
  }
  return res.json();
}

/**
 * Restore a previous version.
 */
export async function restoreVersion(id, version) {
  const res = await authFetch(
    `${API_BASE}/integrations/${encodeURIComponent(id)}/versions/${version}/restore`,
    { method: "POST" }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Restore failed" }));
    throw new Error(extractError(err, "Failed to restore version"));
  }
  return res.json();
}

export { API_BASE, PAYWALL_API_BASE };
